import { ChangeDetectionStrategy, Component, inject, Input, signal, ViewEncapsulation, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RestService } from 'src/app/services/rest.service';
import { SurveyHeaderComponent } from './survey-header/survey-header.component';
import { Subscription } from 'rxjs';
import Swal from "sweetalert2";
import { ToolsService } from 'src/app/services/tools.service';
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';

@Component({
    selector: 'survey',
    standalone: true,
    imports: [
        CommonModule,
        IonicModule,
        TranslateModule,
        ReactiveFormsModule,
        SurveyHeaderComponent
    ],
    templateUrl: './new-survey.component.html',
    styleUrl: './new-survey.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})

export class SurveyComponent{
    @Input('isModal') isModal: boolean = false;
    @Input('registerID') registerID: any = null;
    @Input('surveyModal') surveyModal: any = null;
    

    private tools = inject(ToolsService);
    private routeService = inject(ActivatedRoute);
    private modalController = inject(ModalController);
    private router = inject(Router);
    private translate = inject(TranslateService);
    private rest = inject(RestService);
    private fb = inject(FormBuilder);
    private _matBottomSheetService = inject(MatBottomSheetService);
    private langSubscription: Subscription;
    private surveyId: any;
    private structure: any;
    private greetingsMessage: any;
    
    public languages: any = [];
    public surveyForm: FormGroup;
    public surveyStyle: any;
    public surveyName: any;
    public surveyLogo: any;
    public formData: any;
    public showForm = signal(false)

    ngOnInit(){      
      if (this.isModal){
        if(!this.surveyModal?.structure)
          return;

        this.surveyId = this.surveyModal?.id;
        this.structure = this.surveyModal?.structure;
        this.surveyStyle = this.surveyModal?.style;
        this.surveyName = this.surveyModal?.name;
        this.surveyLogo = this.surveyModal?.style?.logo?.url;
        this.greetingsMessage = this.surveyModal?.greeting;

        this.initForm();
        return;
      }
      
      this.surveyId = this.routeService.snapshot.paramMap.get('id');
      const defaultLang = this.routeService.snapshot.paramMap.get('lang') || this.translate.currentLang || 'es';
      this.getRestInfo(this.surveyId, defaultLang);
    }

    initSurvey() {
      this.langSubscription = this.translate.onLangChange.subscribe((event) => { this.initForm() });
      this.initForm();
    }

    initForm() {
      this.surveyForm = this.fb.group({});
      this.formData = this.structure?.[this.translate.currentLang]?.map((e, index) => {
        let options = [];
        switch (e.type) {
          case "satisfaction_level_3": options = [1, 2, 3]; break;
          case "satisfaction_level_5": options = [1, 2, 3, 4, 5]; break;
        }
        return {
          key: `${index}-${e.type}`,
          type: e.type,
          title: e.name || e.title,
          options: e.options || options,
          required: e.required || false,
        };
      });
      
  
      if (!this.formData) {
        this.formData = [];
        return;
      }
  
      this.formData.forEach((data) => {
        if (data.type !== "selection_multi") {
          // Preparar validadores según el tipo de campo
          const validators = [];
          
          // Agregar validación requerida si el campo es obligatorio
          if (data.required) {
            validators.push(Validators.required);
          }
          
          // Agregar validación específica para campos de email
          if (data.type === 'email') {
            validators.push(Validators.email);
          }
          
          this.surveyForm.addControl(data.key, new FormControl("", validators));
        } 
        else {
          const multi = this.fb.group({ items: this.fb.array([]) });
          const itemsFormArray = multi.get("items") as FormArray;
          data.options.forEach((option) => {
            itemsFormArray.push(this.fb.group({value: option, selected: this.fb.control(false)}));
          });
          
          // Agregar validación personalizada para selection_multi si es requerido
          if (data.required) {
            multi.setValidators(this.multiSelectionValidator);
          }
          
          this.surveyForm.addControl(data.key, multi);
        }
      });
      this.showForm.set(true);
    }

    setSelectedOption(option: any, key: string) {
      this.surveyForm.patchValue({ key, value: option });
    }

    getMultiControls(key) {
      const form = this.surveyForm.get(key);
      const itemsForm = form.get('items') as FormArray
      return itemsForm.controls;
    }

    getFormData(){
      const formArray = Object.values(this.surveyForm.value);
      const array = this.formData?.map((element, index) => ({ ...element, answer: formArray.length > index ? formArray[index] : "" }));

      return array?.map((e) => {
        if (e.type == "selection_multi") 
          e.answer = e.answer?.items?.filter(e => e.selected).map(answer => { return answer.value; });
  
        if (e.type == "satisfaction_level_3") 
          e.answer = (e.answer === "" || e.answer === null) ? null : `${e.answer}/3`;
  
        if (e.type == "satisfaction_level_5") 
          e.answer = (e.answer === "" || e.answer === null) ? null : `${e.answer}/5`;
  
        return { question: e.title, type: e.type, answer: e.answer };
      });
    }

    getRestInfo(id, lang) {
      this.rest.getSurvey(id).subscribe((element) => {
        if (!element.data?.langs?.includes(lang))
          this.translate.use(element?.data?.main_language || 'es');
        else
          this.translate.use(lang);
  
        const survey = element.data || undefined;
        this.surveyStyle = survey?.style;
        this.languages = survey?.langs || [];
        this.tools.setLanguages(survey?.langs || []);
        this.structure = survey?.structure;
        this.greetingsMessage = survey?.greeting;
        this.surveyLogo = survey?.style?.logo?.url;

        if (element.status == "KO" || !survey) {
          this.router.navigateByUrl("not-found");
          return;
        }

        localStorage.setItem("survey", JSON.stringify(survey));
        this.initSurvey();
      });
    }

    sendSurvey() {
      // Verificar que el formulario sea válido antes de enviar
      if (!this.isFormValid) {
        console.warn('Formulario no válido, no se puede enviar');
        return;
      }

      const formData = this.getFormData();
      const send = { 
        register_id: this.registerID, 
        survey_id: this.surveyId, 
        data: formData 
      };
            
      this.rest.saveSurvey(send).subscribe((response) => {
        Swal.fire({
            title: "",
            text: this.translate.instant("Encuesta enviada correctamente"),
            icon: "success",
            showCancelButton: false,
            showConfirmButton: false,
            timer: 10000,
            timerProgressBar: true,
            footer: this.greetingsMessage?.[this.translate.currentLang],
        }).then((result) => {
            if (this.isModal){
              this.dismissModal()
              return;
            }

            if (result.dismiss === Swal.DismissReason.timer || result.dismiss === Swal.DismissReason.backdrop) {
                location.reload();
            }
        });
      });
    }

    dismissModal(){
      this.modalController.dismiss().catch(e => {
          console.warn("No modal found on dismiss")
      });
    }

    // Helper methods for organizing options in columns
    shouldUseColumns(options: any[]): boolean {
      return options.length > 4;
    }

    getFirstColumnOptions(options: any[]): any[] {
      if (!this.shouldUseColumns(options)) return options;
      const firstColumnSize = Math.ceil(options.length / 2);
      return options.slice(0, firstColumnSize);
    }

    getSecondColumnOptions(options: any[]): any[] {
      if (!this.shouldUseColumns(options)) return [];
      const firstColumnSize = Math.ceil(options.length / 2);
      return options.slice(firstColumnSize);
    }

    // Validador personalizado para selection_multi (al menos una opción seleccionada)
    multiSelectionValidator(control: any) {
      const items = control.get('items');
      if (!items || !items.value) return null;
      
      const hasSelection = items.value.some((item: any) => item.selected);
      return hasSelection ? null : { required: true };
    }

    // Getter para verificar si el formulario es válido
    get isFormValid(): boolean {
      return this.surveyForm ? this.surveyForm.valid : false;
    }

    ngOnDestroy(){
      this.langSubscription?.unsubscribe();
    }
}